import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ClipboardList, Quote, History, HelpCircle, ChevronDown, ChevronUp } from "lucide-react";

const ACTIONS = [
  { id: "chiarimento_programma", label: "Chiarimento del programma" },
  { id: "approfondimento_preferenze", label: "Approfondimento delle preferenze" },
  { id: "incontro_orientativo", label: "Incontro orientativo" },
  { id: "presentazione_laboratorio", label: "Presentazione di un laboratorio" },
  { id: "supporto_metodo_studio", label: "Supporto al metodo di studio" },
  { id: "nessuna_azione", label: "Nessuna azione al momento" },
];

const OUTCOMES = [
  { id: "approfondito", label: "Approfondito" },
  { id: "chiarimento_registrato", label: "Chiarimento registrato" },
  { id: "non_approfondito", label: "Non approfondito" },
];

const REASON_LABELS = {
  unanswered: "Senza risposta",
  not_enough_information: "Non ho elementi per rispondere",
};

const COMMISSION_GUIDE = [
  { id: "SC", area: "Scelta del percorso",
    base: "Che cosa ti ha portato a scegliere un ITS e che cosa hai verificato sul percorso?",
    followups: ["Quali attività immagini di svolgere nel ruolo professionale a cui ti prepari?",
                "Quale aspetto del programma vuoi ancora chiarire?"] },
  { id: "PG", area: "Presenza e collaborazione",
    base: "Come organizzeresti il tuo contributo in un progetto con persone che hanno preparazioni diverse?",
    followups: ["Racconta una collaborazione: come avete coordinato i contributi?",
                "Se fossi più avanti in un'attività e più indietro in un'altra, come gestiresti lo scambio con i compagni?"] },
  { id: "TP", area: "Teoria e pratica",
    base: "Come affronteresti un'unità obbligatoria che ti interessa meno delle altre?",
    followups: ["Racconta un'attività poco interessante che hai comunque portato avanti: che cosa hai fatto?",
                "Quando una spiegazione non ti è chiara, quali passi fai?"] },
  { id: "CO", area: "Priorità al completamento",
    base: "Quando un impegno formativo diventa più difficile del previsto, come decidi i passi successivi?",
    followups: ["Racconta un impegno che hai mantenuto nel tempo e cosa ti ha aiutato.",
                "Come valuteresti un'offerta di lavoro incompatibile con la frequenza durante il percorso?"] },
];

function AnswerValue({ answer }) {
  if (!answer) return <span className="text-slate-400">Risposta non fornita</span>;
  if (answer.value) return <span className="font-semibold text-slate-800">{answer.value} su 5</span>;
  return <span className="text-slate-400">{REASON_LABELS[answer.reason] || "Senza risposta"}</span>;
}

export function InterviewPanel({ appId, rubric, interview, history, overall, spunti, onSaved }) {
  const [scores, setScores] = useState({});
  const [notes, setNotes] = useState("");
  const [actions, setActions] = useState([]);
  const [spuntiState, setSpuntiState] = useState({});
  const [changeReason, setChangeReason] = useState("");
  const [saveError, setSaveError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setScores(interview?.scores || {});
    setNotes(interview?.notes_clarified || "");
    setActions(interview?.actions || []);
    setSpuntiState(interview?.spunti || {});
    setChangeReason("");
  }, [interview?.updated_at]);

  const [guideOpen, setGuideOpen] = useState(() => localStorage.getItem("its_commission_guide_open") === "1");
  const toggleGuide = () => {
    setGuideOpen((prev) => {
      localStorage.setItem("its_commission_guide_open", prev ? "0" : "1");
      return !prev;
    });
  };

  const toggleAction = (id) =>
    setActions((prev) => (prev.includes(id) ? prev.filter((a) => a !== id) : [...prev, id]));

  const save = async () => {
    setBusy(true);
    setSaveError("");
    try {
      await api.put(`/admin/applications/${appId}/interview`, {
        scores, notes_clarified: notes, actions, spunti: spuntiState,
        change_reason: changeReason || null,
      });
      toast.success("Dati di colloquio salvati");
      onSaved?.();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setSaveError(typeof detail === "string" ? detail : formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="institutional-card" data-testid="interview-panel">
      <CardContent className="p-6 space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="eyebrow flex items-center gap-2"><ClipboardList className="h-4 w-4" /> Colloquio — valutazione della commissione</p>
          <div data-testid="overall-score" className="text-sm">
            {overall?.complete ? (
              <span className="font-semibold text-slate-900">
                Punteggio complessivo: <span className="font-mono2 text-[#003366]">{overall.total}</span>
                {overall.max_total ? <span className="font-mono2 text-slate-500"> su {overall.max_total}</span> : null}
                <span className="text-xs text-slate-500 font-normal"> (test scritto {overall.test_a} + colloquio {overall.interview_total})</span>
              </span>
            ) : (
              <span className="text-slate-500">
                Punteggio complessivo disponibile dopo il colloquio
                {overall?.test_a != null && (
                  <span className="font-mono2"> — test scritto: {overall.test_a}{overall.test_a_max ? `/${overall.test_a_max}` : ""}</span>
                )}
              </span>
            )}
          </div>
        </div>

        <div className="rounded-lg border border-slate-200" data-testid="commission-guide">
          <button type="button" data-testid="commission-guide-toggle" onClick={toggleGuide}
            className="flex w-full items-center justify-between gap-2 px-4 py-3 text-left transition-colors hover:bg-slate-50">
            <span className="flex items-center gap-2 text-sm font-semibold text-slate-900">
              <HelpCircle className="h-4 w-4 text-[#003366]" /> Guida per la commissione
            </span>
            {guideOpen ? <ChevronUp className="h-4 w-4 shrink-0 text-slate-500" /> : <ChevronDown className="h-4 w-4 shrink-0 text-slate-500" />}
          </button>
          {guideOpen && (
            <div className="border-t border-slate-200 px-4 py-4">
              <p className="mb-3 text-xs text-slate-500">Per ogni area: una domanda di base uguale per tutti e due approfondimenti.</p>
              <div className="space-y-4">
                {COMMISSION_GUIDE.map((g) => (
                  <div key={g.id} data-testid={`commission-guide-${g.id}`}>
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{g.area}</p>
                    <p className="mt-1 text-sm text-slate-800"><span className="font-semibold">Domanda di base:</span> {g.base}</p>
                    <ol className="mt-1 list-decimal pl-5 text-sm text-slate-600 space-y-0.5">
                      {g.followups.map((f) => <li key={f}>{f}</li>)}
                    </ol>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div>
          <p className="text-sm font-semibold text-slate-900">Punteggi di colloquio (rubrica dell'istituto)</p>
          <p className="mt-1 text-xs text-slate-500 leading-relaxed" data-testid="rubric-guidance">
            Valuta le evidenze raccolte nel colloquio secondo la rubrica dell'ITS. Non convertire gli stati della Rotta in punteggi.
          </p>
          <p className="mt-1 mb-3 text-xs text-slate-400 leading-relaxed">
            Non pretendere nomi di aziende o esperienze specifiche: valuta ciò che il candidato ha effettivamente detto o descritto.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {rubric.map((r) => (
              <div key={r.id}>
                <Label className="text-xs">{r.label} <span className="text-slate-400">({r.min}–{r.max})</span></Label>
                <Input data-testid={`interview-score-${r.id}`} type="number" min={r.min} max={r.max} step="1"
                  className="mt-1.5" value={scores[r.id] ?? ""}
                  onChange={(e) => setScores({ ...scores, [r.id]: e.target.value === "" ? undefined : Number(e.target.value) })} />
              </div>
            ))}
          </div>
        </div>

        {spunti?.length > 0 && (
          <div data-testid="spunti-section">
            <p className="text-sm font-semibold text-slate-900 mb-1">Spunti per il colloquio</p>
            <p className="text-xs text-slate-500 mb-3">
              Coppie di affermazioni da confrontare in modo neutro: non indicano contraddizioni né inattendibilità.
            </p>
            <div className="space-y-3">
              {spunti.map((p) => (
                <div key={p.qualitative_code} className="border border-slate-100 rounded-lg p-4" data-testid={`spunti-pair-${p.qualitative_code}`}>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                    <div>
                      <p className="text-slate-600"><span className="font-mono2 text-slate-400 mr-1">{p.direct_code}</span>{p.direct_text}</p>
                      <p className="mt-1"><AnswerValue answer={p.direct_answer} /></p>
                    </div>
                    <div>
                      <p className="text-slate-600"><span className="font-mono2 text-slate-400 mr-1">{p.qualitative_code}</span>{p.qualitative_text}</p>
                      <p className="mt-1"><AnswerValue answer={p.qualitative_answer} /></p>
                    </div>
                  </div>
                  <p className="mt-2 text-xs text-slate-500 flex items-start gap-1.5">
                    <Quote className="h-3 w-3 mt-0.5 shrink-0 text-[#003366]" />
                    <span className="italic">{p.suggestion}</span>
                  </p>
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <select data-testid={`spunti-${p.qualitative_code}-outcome`}
                      className="h-9 rounded-md border border-slate-200 bg-white px-2 text-xs"
                      value={spuntiState[p.qualitative_code]?.outcome || ""}
                      onChange={(e) => setSpuntiState({ ...spuntiState, [p.qualitative_code]: { ...(spuntiState[p.qualitative_code] || {}), outcome: e.target.value } })}>
                      <option value="">Esito…</option>
                      {OUTCOMES.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                    </select>
                    <Input data-testid={`spunti-${p.qualitative_code}-note`} className="h-9 flex-1 min-w-[180px] text-xs"
                      placeholder="Nota sul chiarimento (facoltativa)" maxLength={500}
                      value={spuntiState[p.qualitative_code]?.note || ""}
                      onChange={(e) => setSpuntiState({ ...spuntiState, [p.qualitative_code]: { ...(spuntiState[p.qualitative_code] || {}), note: e.target.value } })} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div>
          <Label className="text-sm font-semibold text-slate-900">Evidenze e aspetti chiariti nel colloquio</Label>
          <Textarea data-testid="interview-notes" rows={4} maxLength={2000} className="mt-2 text-sm"
            placeholder="Annota ciò che il candidato ha detto o descritto durante il colloquio…"
            value={notes} onChange={(e) => setNotes(e.target.value)} />
        </div>

        <div>
          <p className="text-sm font-semibold text-slate-900 mb-2">Eventuali azioni concordate</p>
          <div className="flex flex-wrap gap-2">
            {ACTIONS.map((a) => (
              <button key={a.id} type="button" data-testid={`interview-action-${a.id}`} onClick={() => toggleAction(a.id)}
                className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                  actions.includes(a.id) ? "border-[#003366] bg-[#003366] text-white" : "border-slate-300 text-slate-600 hover:border-slate-400"}`}>
                {a.label}
              </button>
            ))}
          </div>
        </div>

        {interview && (
          <div>
            <Label className="text-xs">Motivo della rettifica (obbligatorio per modificare dati già salvati)</Label>
            <Input data-testid="interview-change-reason" className="mt-1.5 text-sm" maxLength={300}
              value={changeReason} onChange={(e) => setChangeReason(e.target.value)} />
          </div>
        )}

        {saveError && (
          <p role="alert" data-testid="interview-error"
            className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
            {saveError}
          </p>
        )}

        <Button data-testid="interview-save-button" className="bg-[#003366] hover:bg-[#002244]" disabled={busy} onClick={save}>
          {busy ? "Salvataggio..." : "Salva dati colloquio"}
        </Button>

        {history?.length > 0 && (
          <div data-testid="interview-history" className="border-t border-slate-100 pt-4">
            <p className="text-xs font-semibold text-slate-700 mb-2 flex items-center gap-1.5">
              <History className="h-3.5 w-3.5" /> Storico modifiche
            </p>
            <ul className="space-y-1.5">
              {[...history].reverse().map((h, i) => (
                <li key={`${h.at}-${i}`} className="text-xs text-slate-500">
                  <span className="font-mono2">{new Date(h.at).toLocaleString("it-IT")}</span> — {h.by}
                  {h.reason && <Badge variant="secondary" className="ml-2 text-[10px]">Rettifica: {h.reason}</Badge>}
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
